import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoice } from "@fortawesome/free-solid-svg-icons";
import CustomersPageGen from "./generated/CustomersPageGen";
import customersService from "../../../services/legacy/customersService";

const GRID_ORDER = [
  "accountNum",
  "name",
  "vatnum",
  "city",
  "country",
  "phone",
  "email",
  "custgroup",
  "blocked",
  "creditmax",
  "inactive",
];

const FILTER_ORDER = [
  "accountNum",
  "name",
  "vatnum",
  "city",
  "country",
  "custgroup",
  "blocked",
  "inactive",
];

const SHOW_IN_GRID = new Set(GRID_ORDER);
const SHOW_IN_FILTER = new Set(FILTER_ORDER);

const transformColumns = (baseColumns) => {
  const transformed = baseColumns.map((column) => ({
    ...column,
    showInGrid: SHOW_IN_GRID.has(column.key),
    showInFilter: SHOW_IN_FILTER.has(column.key),
  }));

  const orderByPreferredList = (columns, preferredOrder) => {
    const indexMap = new Map(preferredOrder.map((key, index) => [key, index]));
    return [...columns].sort((a, b) => {
      const aIndex = indexMap.has(a.key) ? indexMap.get(a.key) : Number.MAX_SAFE_INTEGER;
      const bIndex = indexMap.has(b.key) ? indexMap.get(b.key) : Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });
  };

  // La rejilla y los filtros usan el orden del array de columnas.
  // Priorizamos el orden deseado para los campos visibles.
  return orderByPreferredList(transformed, GRID_ORDER);
};

const CustomersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const service = useMemo(() => {
    const scopedService = Object.create(customersService);
    scopedService.getAll = async (page = 0, pageSize = 100, sort = [], filters = {}) => {
      const hasExplicitSort = Array.isArray(sort) && sort.length > 0;
      const effectiveSort = hasExplicitSort ? sort : [{ field: "recId", direction: "desc" }];
      return customersService.getAll(page, pageSize, effectiveSort, filters);
    };
    return scopedService;
  }, []);

  // Jumps to Pedidos de Venta pre-filtered by this customer, using the same
  // state+query navigation convention SalesOrderDocumentPage already reads
  // for "salesOrders.viewOrderActions" (see custaccountFilter there).
  const renderRowActions = useCallback((row) => {
    const accountNum = String(row?.accountNum || "").trim();
    if (!accountNum) {
      return null;
    }
    return (
      <FontAwesomeIcon
        icon={faFileInvoice}
        className="action-icon action-edit"
        title={t("customers.viewSalesOrders")}
        aria-label={t("customers.viewSalesOrders")}
        onClick={() => {
          const query = new URLSearchParams({ custaccount: accountNum }).toString();
          navigate(`/legacy/sales-orders?${query}`, {
            state: { initialFilters: { custaccount: accountNum } },
          });
        }}
      />
    );
  }, [navigate, t]);

  return (
    <CustomersPageGen
      service={service}
      transformColumns={transformColumns}
      crudConfigOverride={{
        initialFilters: { inactive: "0" },
        renderRowActions,
      }}
    />
  );
};

export default CustomersPage;
